import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Skeleton,
  SkeletonText,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Spinner
} from "@chakra-ui/react";
import { 
  FiPlus, 
  FiEdit, 
  FiEye, 
  FiShield, 
  FiCreditCard,
  FiMapPin,
  FiDollarSign,
  FiSearch,
  FiUser,
  FiChevronDown
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { 
  useGetEmployeeBankDetails, 
  useGetAllEmployeeBankDetails,
  useAddEmployeeBankDetails,
  useUpdateEmployeeBankDetails,
  useNameEnquiry
} from "../../../services/payrollService";
import { useGetEmployees } from "../../../services/employeeService";
import { showSuccessAlert, showErrorAlert } from "../../../utils/alert";
import { formatCurrency } from "../../../utils/currency";

// Helper function to calculate Rent Relief
const calculateRentRelief = (annualRent) => {
  if (!annualRent || annualRent <= 0) return 0;
  const twentyPercent = annualRent * 0.20;
  const maxRelief = 500000; // ₦500,000 maximum
  return Math.min(maxRelief, twentyPercent);
};

const EmployeeBankDetails = () => {
  const { user } = useUserContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [viewingDetails, setViewingDetails] = useState(null); // For view modal
  const [isEditing, setIsEditing] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [bankSearchTerm, setBankSearchTerm] = useState("");
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const bankDropdownRef = useRef(null);
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
      if (bankDropdownRef.current && !bankDropdownRef.current.contains(event.target)) {
        setShowBankDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Real API data
  const { 
    getEmployeesData: employeesData, 
    getEmployeesIsLoading: employeesLoading, 
    getEmployeesError: employeesError 
  } = useGetEmployees({ enabled: true });
  
  const { 
    data: bankDetailsData, 
    isLoading: bankDetailsLoading, 
    error: bankDetailsError 
  } = useGetEmployeeBankDetails(selectedEmployee?.employee_id);
  
  const { 
    data: allBankDetailsData, 
    isLoading: allBankDetailsLoading, 
    error: allBankDetailsError 
  } = useGetAllEmployeeBankDetails({ page: 1, limit: 50 });
  
  const addBankDetailsMutation = useAddEmployeeBankDetails();
  const updateBankDetailsMutation = useUpdateEmployeeBankDetails();
  const nameEnquiryMutation = useNameEnquiry();
  
  // Process API data
  const employees = employeesData?.employees || [];
  const bankDetails = Array.isArray(bankDetailsData?.data) ? bankDetailsData.data : 
                     Array.isArray(bankDetailsData) ? bankDetailsData : [];
  
  // Process all bank details for table view
  const allBankDetails = allBankDetailsData?.data?.bank_details || allBankDetailsData?.bank_details || [];
  
  
  // Nigerian banks list
  const nigerianBanks = [
    { code: "044", name: "Access Bank" },
    { code: "058", name: "GTBank" },
    { code: "033", name: "UBA" },
    { code: "214", name: "First Bank" },
    { code: "011", name: "First Bank" },
    { code: "050", name: "Ecobank" },
    { code: "070", name: "Fidelity Bank" },
    { code: "221", name: "Stanbic IBTC" },
    { code: "068", name: "Standard Chartered" },
    { code: "232", name: "Sterling Bank" },
    { code: "032", name: "Union Bank" },
    { code: "215", name: "Unity Bank" },
    { code: "035", name: "Wema Bank" },
    { code: "057", name: "Zenith Bank" },
    { code: "076", name: "Polaris Bank" },
    { code: "082", name: "Keystone Bank" },
    { code: "090", name: "VFD Microfinance Bank" },
    { code: "100", name: "Suntrust Bank" },
    { code: "101", name: "Providus Bank" },
    { code: "102", name: "Titan Trust Bank" },
    { code: "103", name: "Globus Bank" },
    { code: "104", name: "Parallex Bank" },
    { code: "105", name: "Premium Trust Bank" },
    { code: "106", name: "Optimus Bank" },
    { code: "107", name: "Moniepoint Microfinance Bank" },
    { code: "108", name: "Opay" },
    { code: "109", name: "Kuda Bank" },
    { code: "110", name: "VBank" },
    { code: "111", name: "Mint Microfinance Bank" },
    { code: "112", name: "M42 Microfinance Bank" },
    { code: "113", name: "Carbon" },
    { code: "114", name: "Fairmoney Microfinance Bank" },
    { code: "115", name: "Sparkle Microfinance Bank" },
    { code: "116", name: "Kredi Money Microfinance Bank" },
    { code: "117", name: "9 Payment Service Bank" },
    { code: "118", name: "Airtel Smartcash PSB" },
    { code: "119", name: "MTN Momo PSB" },
    { code: "120", name: "PalmPay" }
  ];

  // Get list of employee IDs that already have bank details
  const employeesWithBankDetails = new Set(
    allBankDetails.map(detail => detail.employee_id)
  );

  // Filter employees based on search term AND exclude those with existing bank details (for new entries)
  const filteredEmployees = employees.filter(employee => {
    // Check if matches search term
    const matchesSearch = 
      employee.first_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      employee.last_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      employee.employee_id?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(employeeSearchTerm.toLowerCase());
    
    // For editing, show all matching employees
    // For adding new, exclude employees who already have bank details
    if (isEditing) {
      return matchesSearch;
    }
    
    return matchesSearch && !employeesWithBankDetails.has(employee.employee_id);
  });

  // Filter banks based on search term
  const filteredBanks = nigerianBanks.filter(bank =>
    bank.name?.toLowerCase().includes(bankSearchTerm.toLowerCase()) ||
    bank.code?.toLowerCase().includes(bankSearchTerm.toLowerCase())
  );

  const [formData, setFormData] = useState({
    employee_id: "",
    account_number: "",
    bank_code: "",
    bank_name: "",
    account_name: "",
    country_code: "NG",
    currency: "NGN",
    routing_number: "",
    tax_percentage: 15.0,
    tax_calculation_method: "progressive", // 'progressive' or 'flat'
    annual_rent: 0
  });
  
  const [isVerifyingAccount, setIsVerifyingAccount] = useState(false);
  const [accountVerificationError, setAccountVerificationError] = useState(null);
  const [originalBankDetails, setOriginalBankDetails] = useState({ bank_code: null, account_number: null });

  const countries = [
    { code: "NG", name: "Nigeria", currency: "NGN" },
    { code: "US", name: "United States", currency: "USD" },
    { code: "GH", name: "Ghana", currency: "GHS" },
    { code: "KE", name: "Kenya", currency: "KES" },
    { code: "ZA", name: "South Africa", currency: "ZAR" }
  ];

  const handleAddBankDetails = () => {
    handleAddNew();
  };

  const handleEditBankDetails = (bankDetail) => {
    // Find the employee from the employees list
    const employee = employees.find(emp => emp.employee_id === bankDetail.employee_id);
    setSelectedEmployee(employee);
    setIsEditing(true);
    
    // Store original values to detect changes during editing
    setOriginalBankDetails({
      bank_code: bankDetail.bank_code,
      account_number: bankDetail.account_number
    });
    
    // Determine default tax method if not provided
    const defaultTaxMethod = bankDetail.country_code === "NG" ? "progressive" : "flat";
    
    setFormData({
      employee_id: bankDetail.employee_id,
      account_number: bankDetail.account_number,
      bank_code: bankDetail.bank_code,
      bank_name: bankDetail.bank_name,
      account_name: bankDetail.account_name,
      country_code: bankDetail.country_code || "NG",
      currency: bankDetail.currency || "NGN",
      routing_number: bankDetail.routing_number || "",
      tax_percentage: bankDetail.tax_percentage || (defaultTaxMethod === "flat" ? 15.0 : 0),
      tax_calculation_method: bankDetail.tax_calculation_method || defaultTaxMethod,
      annual_rent: bankDetail.annual_rent || 0
    });
    // Set bank search term for display
    if (bankDetail.bank_name && bankDetail.bank_code) {
      setBankSearchTerm(`${bankDetail.bank_name} (${bankDetail.bank_code})`);
    }
    // Set employee search term for display
    if (employee) {
      setEmployeeSearchTerm(`${employee.first_name} ${employee.last_name} (${employee.employee_id})`);
    }
    onOpen();
  };

  const handleSaveBankDetails = () => {
    // Validation - ensure tax_calculation_method has a value (default to progressive for NG)
    const taxMethod = formData.tax_calculation_method || 
      (formData.country_code === "NG" ? "progressive" : "flat");
    
    if (taxMethod === "flat" && (!formData.tax_percentage || formData.tax_percentage <= 0)) {
      showErrorAlert("Tax percentage is required for flat rate");
      return;
    }
    
    if (formData.annual_rent && formData.annual_rent < 0) {
      showErrorAlert("Annual rent cannot be negative");
      return;
    }
    
    const bankDetailsPayload = {
      ...formData,
      tax_calculation_method: taxMethod, // Ensure it's always set
      employee_id: selectedEmployee?.employee_id || formData.employee_id
    };
    
    const mutation = isEditing ? updateBankDetailsMutation : addBankDetailsMutation;
    
    mutation.mutate(bankDetailsPayload, {
      onSuccess: () => {
        // Show success alert
        if (isEditing) {
          showSuccessAlert("Bank details updated successfully!");
        } else {
          showSuccessAlert("Bank details added successfully!");
        }
        
    onClose();
        setFormData({
          employee_id: "",
          account_number: "",
          bank_code: "",
          bank_name: "",
          account_name: "",
          country_code: "NG",
          currency: "NGN",
          routing_number: "",
          tax_percentage: 15.0,
          tax_calculation_method: "progressive",
          annual_rent: 0
        });
        setSelectedEmployee(null);
        setEmployeeSearchTerm("");
        setBankSearchTerm("");
        setIsEditing(false);
        setAccountVerificationError(null);
        setIsVerifyingAccount(false);
        setOriginalBankDetails({ bank_code: null, account_number: null });
      },
      onError: (error) => {
        console.error("Error saving bank details:", error);
        showErrorAlert("Failed to save bank details. Please try again.");
      }
    });
  };

  const handleCountryChange = (countryCode) => {
    const country = countries.find(c => c.code === countryCode);
    // Auto-set tax calculation method based on country
    const taxMethod = countryCode === "NG" ? "progressive" : "flat";
    setFormData(prev => ({
      ...prev,
      country_code: countryCode,
      currency: country?.currency || "USD",
      tax_calculation_method: taxMethod,
      // Reset annual_rent if switching away from Nigeria
      annual_rent: countryCode === "NG" ? prev.annual_rent : 0
    }));
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setFormData(prev => ({
      ...prev,
      employee_id: employee.employee_id
    }));
    setEmployeeSearchTerm(`${employee.first_name} ${employee.last_name} (${employee.employee_id})`);
    setShowEmployeeDropdown(false);
  };

  const handleBankSelect = (bank) => {
    setFormData(prev => ({
      ...prev,
      bank_code: bank.code,
      bank_name: bank.name,
      account_name: "" // Clear account name when bank changes
    }));
    setBankSearchTerm(`${bank.name} (${bank.code})`);
    setShowBankDropdown(false);
    setAccountVerificationError(null);
    
    // Trigger name enquiry if account number is already filled
    if (formData.account_number && formData.account_number.length >= 10) {
      triggerNameEnquiry(bank.code, formData.account_number);
    }
  };
  
  // Auto-trigger name enquiry when both bank_code and account_number are available
  useEffect(() => {
    // Only trigger for Nigeria and when both fields are filled
    if (
      formData.country_code === "NG" &&
      formData.bank_code &&
      formData.account_number &&
      formData.account_number.length >= 10
    ) {
      // For new entries, always verify
      // For editing, verify if:
      //   1. account_name is empty (needs verification), OR
      //   2. bank_code or account_number changed from original values (user modified them)
      const bankChanged = isEditing && originalBankDetails.bank_code && 
                         formData.bank_code !== originalBankDetails.bank_code;
      const accountChanged = isEditing && originalBankDetails.account_number && 
                            formData.account_number !== originalBankDetails.account_number;
      const needsVerification = !isEditing || !formData.account_name || bankChanged || accountChanged;
      
      if (needsVerification) {
        // Debounce the API call
        const timeoutId = setTimeout(() => {
          triggerNameEnquiry(formData.bank_code, formData.account_number);
        }, 1000); // Wait 1 second after user stops typing
        
        return () => clearTimeout(timeoutId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.bank_code, formData.account_number, formData.country_code, isEditing, originalBankDetails]);
  
  const triggerNameEnquiry = async (bankCode, accountNumber) => {
    if (!bankCode || !accountNumber || accountNumber.length < 10) {
      return;
    }
    
    setIsVerifyingAccount(true);
    setAccountVerificationError(null);
    
    try {
      const result = await nameEnquiryMutation.mutateAsync({
        account_number: accountNumber,
        bank_code: bankCode
      });
      
      if (result.success && result.account_details?.verified) {
        setFormData(prev => ({
          ...prev,
          account_name: result.account_details.account_name || "",
          bank_name: result.account_details.bank_name || prev.bank_name
        }));
        setAccountVerificationError(null);
      } else {
        setAccountVerificationError(result.message || "Account verification failed");
        setFormData(prev => ({
          ...prev,
          account_name: ""
        }));
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          "Failed to verify account. Please check the account number and try again.";
      setAccountVerificationError(errorMessage);
      setFormData(prev => ({
        ...prev,
        account_name: ""
      }));
    } finally {
      setIsVerifyingAccount(false);
    }
  };

  const handleAddNew = () => {
    setSelectedEmployee(null);
    setEmployeeSearchTerm("");
    setBankSearchTerm("");
    setFormData({
      employee_id: "",
      account_number: "",
      bank_code: "",
      bank_name: "",
      account_name: "",
      country_code: "NG",
      currency: "NGN",
      routing_number: "",
      tax_percentage: 0
    });
    setIsEditing(false);
    setAccountVerificationError(null);
    setIsVerifyingAccount(false);
    setOriginalBankDetails({ bank_code: null, account_number: null });
    onOpen();
  };

  const getCountryName = (code) => {
    return countries.find(c => c.code === code)?.name || code;
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      NGN: "₦",
      USD: "$",
      GHS: "₵",
      KES: "KSh",
      ZAR: "R"
    };
    return symbols[currency] || currency;
  };

  // Show loading state
  if (employeesLoading || allBankDetailsLoading) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.800">
              Employee Bank Details
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Loading employee data...
            </Text>
          </VStack>
        </Flex>
        <Box textAlign="center" py={8}>
          <Text color="gray.500">Loading employee bank details...</Text>
        </Box>
      </VStack>
    );
  }

  // Show error state
  if (employeesError || allBankDetailsError) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.800">
              Employee Bank Details
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Error loading data
            </Text>
          </VStack>
        </Flex>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading data!</AlertTitle>
          <AlertDescription>
            {employeesError?.message || allBankDetailsError?.message || 'Failed to load data'}
          </AlertDescription>
        </Alert>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          {/* <Heading size="md" color="gray.800">
            Employee Bank Details
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Manage employee banking information for payroll processing
          </Text> */}
        </VStack>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={handleAddBankDetails}
          size="md"
          borderRadius="lg"
        >
          Add Bank Details
        </Button>
      </Flex>

      {/* Security Notice */}
      <Alert status="info" borderRadius="lg">
        <AlertIcon />
        <Box>
          <AlertTitle>Secure Storage</AlertTitle>
          <AlertDescription>
            All bank details are encrypted and stored securely. Only authorized personnel can view this information.
          </AlertDescription>
        </Box>
      </Alert>

      {/* Bank Details Table */}
      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <TableContainer>
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Employee</Th>
                <Th>Bank</Th>
                <Th>Account Number</Th>
                <Th>Tax Method</Th>
                <Th>Country</Th>
                <Th>Currency</Th>
                <Th>Last Updated</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {allBankDetails.map((detail) => (
                <Tr key={detail.employee_id}>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="600" color="gray.800">
                        {detail.employee_name}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {detail.employee_id}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="500" color="gray.800">
                        {detail.bank_name}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Code: {detail.bank_code}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontFamily="mono" color="gray.800">
                      ****{detail.account_number.slice(-4)}
                    </Text>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Badge 
                        colorScheme={detail.tax_calculation_method === "progressive" ? "green" : "blue"} 
                        variant="subtle"
                      >
                        {detail.tax_calculation_method === "progressive" 
                          ? "Progressive Tax" 
                          : `Flat (${detail.tax_percentage || 0}%)`}
                      </Badge>
                      {detail.annual_rent && detail.annual_rent > 0 && (
                        <Text fontSize="xs" color="gray.500">
                          Rent: {formatCurrency(detail.annual_rent, (detail.country_code || detail.country) === "NG" ? "Nigeria" : null)}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue" variant="subtle">
                      {getCountryName(detail.country_code || detail.country)}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Text>{getCurrencySymbol(detail.currency)}</Text>
                      <Text>{detail.currency}</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">
                      {new Date(detail.updated_at).toLocaleDateString()}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="View Details">
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="View details"
                          onClick={() => {
                            setViewingDetails(detail);
                            onViewOpen();
                          }}
                        />
                      </Tooltip>
                      <Tooltip label="Edit Details">
                        <IconButton
                          icon={<FiEdit />}
                          size="sm"
                          variant="ghost"
                          colorScheme="orange"
                          aria-label="Edit details"
                          onClick={() => handleEditBankDetails(detail)}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add/Edit Bank Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Bank Details" : "Add Bank Details"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Select Employee</FormLabel>
                <Box position="relative" ref={dropdownRef}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <FiSearch color="gray.300" />
                    </InputLeftElement>
                    <Input
                      value={employeeSearchTerm}
                      onChange={(e) => {
                        setEmployeeSearchTerm(e.target.value);
                        setShowEmployeeDropdown(true);
                      }}
                      onFocus={() => setShowEmployeeDropdown(true)}
                          placeholder="Search employees by name or ID..."
                    />
                  </InputGroup>
                  
                  {showEmployeeDropdown && filteredEmployees.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      boxShadow="lg"
                      zIndex={1000}
                      maxHeight="200px"
                      overflowY="auto"
                    >
                      {/* Debug: Show count */}
                      <Box p={2} bg="gray.100" fontSize="xs">
                        Found {filteredEmployees.length} employees
                      </Box>
                      {filteredEmployees.slice(0, 10).map((employee) => (
                        <Box
                          key={employee.employee_id}
                          p={3}
                          cursor="pointer"
                          _hover={{ bg: "gray.50" }}
                          onClick={() => handleEmployeeSelect(employee)}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                        >
                          <Text fontWeight="medium">{employee.first_name} {employee.last_name}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {employee.employee_id}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Account Number</FormLabel>
                <InputGroup>
                <Input
                  value={formData.account_number}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, account_number: e.target.value, account_name: "" }));
                      setAccountVerificationError(null);
                    }}
                  placeholder="Enter account number"
                    isDisabled={isVerifyingAccount}
                  />
                  {isVerifyingAccount && (
                    <InputRightElement>
                      <Spinner size="sm" color="blue.500" />
                    </InputRightElement>
                  )}
                </InputGroup>
                {accountVerificationError && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {accountVerificationError}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Country</FormLabel>
                <Select
                  value={formData.country_code}
                  onChange={(e) => handleCountryChange(e.target.value)}
                >
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name} ({country.currency})
                    </option>
                  ))}
                </Select>
              </FormControl>

              {formData.country_code === "NG" && (
                <FormControl isRequired>
                  <FormLabel>Bank</FormLabel>
                  <Box position="relative" ref={bankDropdownRef}>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <FiSearch color="gray.300" />
                      </InputLeftElement>
                      <Input
                        value={bankSearchTerm}
                    onChange={(e) => {
                          setBankSearchTerm(e.target.value);
                          setShowBankDropdown(true);
                        }}
                        onFocus={() => setShowBankDropdown(true)}
                        placeholder="Search banks by name or code..."
                      />
                    </InputGroup>
                    
                    {showBankDropdown && filteredBanks.length > 0 && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        boxShadow="lg"
                        zIndex={1000}
                        maxHeight="200px"
                        overflowY="auto"
                      >
                        {/* Debug: Show count */}
                        <Box p={2} bg="gray.100" fontSize="xs">
                          Found {filteredBanks.length} banks
                        </Box>
                        {filteredBanks.slice(0, 15).map((bank) => (
                          <Box
                            key={bank.code}
                            p={3}
                            cursor="pointer"
                            _hover={{ bg: "gray.50" }}
                            onClick={() => handleBankSelect(bank)}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                          >
                            <Text fontWeight="medium">{bank.name}</Text>
                            <Text fontSize="sm" color="gray.600">
                              Code: {bank.code}
                            </Text>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </FormControl>
              )}

              <FormControl isRequired>
                <FormLabel>Account Name</FormLabel>
                <Input
                  value={formData.account_name}
                  readOnly
                  isDisabled
                  placeholder="Account name will be verified automatically"
                  bg="gray.50"
                  _disabled={{
                    opacity: 1,
                    cursor: "not-allowed"
                  }}
                />
                {formData.account_name && (
                  <Text fontSize="xs" color="green.600" mt={1}>
                    ✓ Account verified successfully
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Routing Number (Optional)</FormLabel>
                <Input
                  value={formData.routing_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, routing_number: e.target.value }))}
                  placeholder="Enter routing number"
                />
              </FormControl>

              {/* Tax Calculation Method */}
              <FormControl isRequired>
                <FormLabel>Tax Calculation Method</FormLabel>
                <Select
                  value={formData.tax_calculation_method}
                  onChange={(e) => {
                    const newMethod = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      tax_calculation_method: newMethod,
                      // Reset annual_rent if switching away from progressive
                      annual_rent: newMethod === "progressive" && prev.country_code === "NG" ? prev.annual_rent : 0
                    }));
                  }}
                  isDisabled={formData.country_code === "NG"} // Lock for Nigeria
                >
                  <option value="progressive">Progressive Tax</option>
                  <option value="flat">Flat Rate (Other Countries)</option>
                </Select>
                {formData.country_code === "NG" && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Nigeria requires progressive tax calculation by law
                  </Text>
                )}
                {formData.country_code !== "NG" && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Use flat rate for countries outside Nigeria
                  </Text>
                )}
              </FormControl>

              {/* Tax Percentage - Only for Flat Rate */}
              {formData.tax_calculation_method === "flat" && (
                <FormControl isRequired>
                  <FormLabel>Tax Percentage (%)</FormLabel>
                  <Input
                    type="number"
                    value={formData.tax_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, tax_percentage: parseFloat(e.target.value) || 0 }))}
                    placeholder="15.0"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Enter tax percentage (e.g., 15 for 15%)
                  </Text>
                </FormControl>
              )}

              {/* Annual Rent - Only for Progressive + Nigeria */}
              {formData.tax_calculation_method === "progressive" && formData.country_code === "NG" && (
                <FormControl>
                  <FormLabel>Annual Rent (for Rent Relief)</FormLabel>
                  <Input
                    type="number"
                    value={formData.annual_rent}
                    onChange={(e) => setFormData(prev => ({ ...prev, annual_rent: parseFloat(e.target.value) || 0 }))}
                    placeholder="Enter annual rent (optional)"
                    min="0"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Enter annual rent to calculate Rent Relief (lower of ₦500,000 or 20% of rent). This reduces taxable income.
                  </Text>
                  {formData.annual_rent > 0 && (
                    <Alert status="info" borderRadius="md" mt={2} fontSize="sm">
                      <AlertIcon />
                      <Box>
                        <Text fontWeight="600">
                          Rent Relief: {formatCurrency(calculateRentRelief(formData.annual_rent), "Nigeria")}
                        </Text>
                        <Text fontSize="xs" mt={1}>
                          This amount will reduce your taxable income
                        </Text>
                      </Box>
                    </Alert>
                  )}
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveBankDetails}>
              {isEditing ? "Update" : "Save"} Bank Details
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Bank Details Modal (Read-Only) */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <FiEye />
              <Text>Bank Details</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingDetails && (
              <VStack spacing={4} align="stretch">
                {/* Employee Info */}
                <Box bg="gray.50" p={4} borderRadius="md">
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="600" mb={2}>
                    Employee Information
                  </Text>
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <Text fontSize="sm" color="gray.500">Name</Text>
                      <Text fontWeight="600">{viewingDetails.employee_name}</Text>
                    </Box>
                    <Box flex={1}>
                      <Text fontSize="sm" color="gray.500">Employee ID</Text>
                      <Text fontWeight="600">{viewingDetails.employee_id}</Text>
                    </Box>
                  </HStack>
                </Box>

                {/* Bank Info */}
                <Box bg="blue.50" p={4} borderRadius="md">
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="600" mb={2}>
                    Bank Information
                  </Text>
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontSize="sm" color="gray.500">Bank Name</Text>
                      <Text fontWeight="600">{viewingDetails.bank_name}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.500">Bank Code</Text>
                      <Text fontWeight="600">{viewingDetails.bank_code}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.500">Account Number</Text>
                      <Text fontWeight="600" fontFamily="mono">
                        ****{viewingDetails.account_number?.slice(-4)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.500">Account Name</Text>
                      <Text fontWeight="600">{viewingDetails.account_name}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                {/* Location Info */}
                <Box bg="green.50" p={4} borderRadius="md">
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="600" mb={2}>
                    Location & Currency
                  </Text>
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontSize="sm" color="gray.500">Country</Text>
                      <Badge colorScheme="blue" variant="subtle">
                        {getCountryName(viewingDetails.country_code || viewingDetails.country)}
                      </Badge>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.500">Currency</Text>
                      <HStack spacing={1}>
                        <Text fontWeight="600">{getCurrencySymbol(viewingDetails.currency)}</Text>
                        <Text fontWeight="600">{viewingDetails.currency}</Text>
                      </HStack>
                    </Box>
                  </SimpleGrid>
                </Box>

                {/* Tax Info */}
                <Box bg="orange.50" p={4} borderRadius="md">
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="600" mb={2}>
                    Tax Information
                  </Text>
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontSize="sm" color="gray.500">Tax Calculation Method</Text>
                      <Badge 
                        colorScheme={viewingDetails.tax_calculation_method === "progressive" ? "green" : "blue"} 
                        variant="subtle"
                      >
                        {viewingDetails.tax_calculation_method === "progressive" 
                          ? "Progressive Tax" 
                          : `Flat Rate (${viewingDetails.tax_percentage || 0}%)`}
                      </Badge>
                    </Box>
                    {viewingDetails.tax_calculation_method === "flat" && (
                      <Box>
                        <Text fontSize="sm" color="gray.500">Tax Percentage</Text>
                        <Text fontWeight="600">{viewingDetails.tax_percentage || 0}%</Text>
                      </Box>
                    )}
                    {viewingDetails.tax_calculation_method === "progressive" && viewingDetails.annual_rent > 0 && (
                      <>
                        <Box>
                          <Text fontSize="sm" color="gray.500">Annual Rent</Text>
                          <Text fontWeight="600">
                            {formatCurrency(viewingDetails.annual_rent, (viewingDetails.country_code || viewingDetails.country) === "NG" ? "Nigeria" : null)}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.500">Rent Relief</Text>
                          <Text fontWeight="600" color="green.600">
                            {formatCurrency(calculateRentRelief(viewingDetails.annual_rent), "Nigeria")}
                          </Text>
                        </Box>
                      </>
                    )}
                  </SimpleGrid>
                </Box>

                {/* Metadata */}
                <Box borderTop="1px solid" borderColor="gray.200" pt={3}>
                  <HStack justify="space-between" fontSize="sm" color="gray.500">
                    <Text>Last Updated: {new Date(viewingDetails.updated_at).toLocaleDateString()}</Text>
                    {viewingDetails.routing_number && (
                      <Text>Routing: {viewingDetails.routing_number}</Text>
                    )}
                  </HStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="outline" 
              mr={3} 
              onClick={onViewClose}
            >
              Close
            </Button>
            <Button 
              colorScheme="orange" 
              leftIcon={<FiEdit />}
              onClick={() => {
                onViewClose();
                handleEditBankDetails(viewingDetails);
              }}
            >
              Edit Details
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default EmployeeBankDetails;